import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axiosInstance';
import { ENDPOINTS } from '../api/config';
import { useAuth } from './AuthContext';

const STORAGE_PREFIX = 'laptura_measurement_system';

function validSystem(value) {
  return value === 'kmh' || value === 'mph';
}

function storageKey(user) {
  const identity = user?.id ?? user?.email?.toLowerCase() ?? 'guest';
  return `${STORAGE_PREFIX}:${identity}`;
}

function readStoredSystem(key) {
  try {
    const stored = localStorage.getItem(key);
    if (validSystem(stored)) return stored;
  } catch (_) {}
  return 'mph';
}

const MeasurementSystemContext = createContext(null);

export function MeasurementSystemProvider({ children }) {
  const { user } = useAuth();
  const key = storageKey(user);
  const [measurementSystem, setMeasurementSystemState] = useState(() => readStoredSystem(key));
  const selectionVersion = useRef(0);

  useEffect(() => {
    setMeasurementSystemState(readStoredSystem(key));
    if (!user) return undefined;

    let active = true;
    const requestVersion = selectionVersion.current;
    api.get(ENDPOINTS.preferences)
      .then(({ data }) => {
        if (!active || selectionVersion.current !== requestVersion) return;
        const saved = data?.measurement_system ?? data?.speed_unit;
        if (!validSystem(saved)) return;
        setMeasurementSystemState(saved);
        try {
          localStorage.setItem(key, saved);
        } catch (_) {}
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [key, user]);

  useEffect(() => {
    const syncAcrossTabs = (event) => {
      if (event.key === key && validSystem(event.newValue)) {
        selectionVersion.current += 1;
        setMeasurementSystemState(event.newValue);
      }
    };
    window.addEventListener('storage', syncAcrossTabs);
    return () => window.removeEventListener('storage', syncAcrossTabs);
  }, [key]);

  const setMeasurementSystem = useCallback((system) => {
    if (!validSystem(system)) return;
    selectionVersion.current += 1;
    setMeasurementSystemState(system);
    try {
      localStorage.setItem(key, system);
    } catch (_) {}
    if (user) {
      api.put(ENDPOINTS.preferences, { measurement_system: system }).catch(() => {});
    }
  }, [key, user]);

  const value = useMemo(() => ({
    measurementSystem,
    setMeasurementSystem,
    speedUnit: measurementSystem,
    setSpeedUnit: setMeasurementSystem,
  }), [measurementSystem, setMeasurementSystem]);

  return (
    <MeasurementSystemContext.Provider value={value}>
      {children}
    </MeasurementSystemContext.Provider>
  );
}

export function useMeasurementSystem() {
  const context = useContext(MeasurementSystemContext);
  if (!context) {
    return {
      measurementSystem: 'mph',
      setMeasurementSystem: () => {},
      speedUnit: 'mph',
      setSpeedUnit: () => {},
    };
  }
  return context;
}

// Compatibility names for speed-only telemetry consumers.
export const SpeedUnitProvider = MeasurementSystemProvider;
export const useSpeedUnit = useMeasurementSystem;
